import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { omit, pick } from 'lodash'

import { useQueryParams } from '../../core/hooks'
import { isBooleanTrue } from '../../core/utils'

import { ALL_SELECT_OPTION_ITEM, ROLES } from '../../common/constants'

import { useUserHasRoles } from '../../account/hooks'
import {
  DEFAULT_MENU_CURATION_TASK_LIST_FILTERS,
  MENU_AVAILABILITY_FILTER,
  MENU_CURATION_TASK_SORT_BY,
  MENU_CURATION_TASK_STATUS,
} from '../constants'
import {
  updateMenuCurationTasksNonUrlFilters,
  updateMenuCurationTasksPersistedFilters,
} from '../redux/actions'

const useMenuCurationTasksListFilters = () => {
  const persistedFilters = useSelector(
    store => store.menuCurationTasks.menuCurationTasks.persistedFilters,
  )
  const nonUrlFilters = useSelector(
    store => store.menuCurationTasks.menuCurationTasks.nonUrlFilters,
  )
  const dispatch = useDispatch()
  const isUserCurator = useUserHasRoles([
    ROLES.CURATOR.value,
    ROLES.QA_TRAINEE.value,
  ])
  const isUserQa = useUserHasRoles([ROLES.QA.value, ROLES.QC_TRAINEE.value])
  const isUserQc = useUserHasRoles([ROLES.QC.value])
  const isUserScrapingReviewer = useUserHasRoles([ROLES.SCRAPING.value])

  const {
    currentPage,
    pageSize,
    sortBy,
    qaDetailLevel = persistedFilters?.qaDetailLevel,
    taskStatus,
    isCurrentUserAssigned,
    isCurrentUserAllocated,
    searchText = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.SEARCH,
    assignedTierOneCurator = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.ASSIGNED_TIER_ONE_CURATOR,
    assignedCurator = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.ASSIGNED_CURATOR,
    assignedQa = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.ASSIGNED_QA,
    assignedQc = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.ASSIGNED_QC,
    assignedUnknownWordsCurator = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.ASSIGNED_CURATOR,
    assignedUnknownWordsQa = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.ASSIGNED_CURATOR,
    assignedScrapingReviewerUser = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.ASSIGNED_CURATOR,
    languageIds = persistedFilters?.languageIds,
    duplicate = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.DUPLICATE,
    curatorId = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.CURATOR_ID,
    qaId = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.QA_ID,
    backlog = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.BACKLOG,
    menuAvailability,
    setQueryParams,
  } = useQueryParams()

  const {
    autoeatStatus = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.AUTOEAT_STATUS,
    tags = DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.TAGS,
  } = nonUrlFilters

  const defaultSortBy = useMemo(() => {
    if (isUserCurator) {
      return MENU_CURATION_TASK_SORT_BY.DEFAULT.value
    }

    if (isUserQa) {
      return MENU_CURATION_TASK_SORT_BY.QA.value
    }

    if (isUserQc) {
      return MENU_CURATION_TASK_SORT_BY.QC.value
    }

    return (
      persistedFilters?.sortBy ||
      DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.SORT_BY
    )
  }, [isUserCurator, isUserQa, isUserQc, persistedFilters?.sortBy])

  const defaultMenuAvailability = useMemo(() => {
    if (
      isUserCurator &&
      !isBooleanTrue(isCurrentUserAssigned) &&
      !isBooleanTrue(isCurrentUserAllocated)
    ) {
      return MENU_AVAILABILITY_FILTER.AVAILABLE_FOR_CURATION.value
    }

    if (
      isUserQa &&
      !isBooleanTrue(isCurrentUserAssigned) &&
      !isBooleanTrue(isCurrentUserAllocated)
    ) {
      return MENU_AVAILABILITY_FILTER.AVAILABLE_FOR_QA.value
    }

    if (
      isUserQc &&
      !isBooleanTrue(isCurrentUserAssigned) &&
      !isBooleanTrue(isCurrentUserAllocated)
    ) {
      return MENU_AVAILABILITY_FILTER.AVAILABLE_FOR_QC.value
    }

    if (
      isUserScrapingReviewer &&
      !isBooleanTrue(isCurrentUserAssigned) &&
      !isBooleanTrue(isCurrentUserAllocated)
    ) {
      return ALL_SELECT_OPTION_ITEM.value
    }

    return persistedFilters?.menuAvailability || ALL_SELECT_OPTION_ITEM.value
  }, [
    isUserCurator,
    isCurrentUserAssigned,
    isCurrentUserAllocated,
    isUserQa,
    isUserQc,
    isUserScrapingReviewer,
    persistedFilters?.menuAvailability,
  ])

  const defaultTaskStatus = useMemo(() => {
    if (isUserScrapingReviewer) {
      return MENU_CURATION_TASK_STATUS.AWAITING_SCRAPING_REVIEW.value
    }

    return (
      persistedFilters?.taskStatus ||
      DEFAULT_MENU_CURATION_TASK_LIST_FILTERS.TASK_STATUS
    )
  }, [isUserScrapingReviewer, persistedFilters?.taskStatus])

  const setFilters = useCallback(
    value => {
      dispatch(
        updateMenuCurationTasksPersistedFilters({
          ...persistedFilters,
          ...pick(value, [
            'sortBy',
            'qaDetailLevel',
            'taskStatus',
            'menuAvailability',
            'languageIds',
            'duplicate',
            'tags',
          ]),
        }),
      )

      dispatch(
        updateMenuCurationTasksNonUrlFilters({
          ...nonUrlFilters,
          ...pick(value, ['tags', 'autoeatStatus']),
        }),
      )

      setQueryParams(omit(value, ['tags', 'autoeatStatus']))
    },
    [dispatch, nonUrlFilters, persistedFilters, setQueryParams],
  )

  return {
    currentPage,
    pageSize,
    sortBy: sortBy || defaultSortBy,
    qaDetailLevel,
    taskStatus: taskStatus || defaultTaskStatus,
    menuAvailability: menuAvailability || defaultMenuAvailability,
    autoeatStatus,
    searchText,
    assignedScrapingReviewerUser,
    assignedTierOneCurator,
    assignedCurator,
    assignedQa,
    assignedQc,
    assignedUnknownWordsCurator,
    assignedUnknownWordsQa,
    languageIds,
    duplicate,
    backlog,
    tags,
    curatorId,
    qaId,
    isCurrentUserAssigned,
    isCurrentUserAllocated,
    setFilters,
  }
}

export default useMenuCurationTasksListFilters
